import { useState } from 'react';
import { CheckIcon, ClipboardIcon } from '../icons.tsx';
import type { FieldRow, InventorySettings } from '../../lib/inventory.ts';

/**
 * How to drive the inventory from a script.
 *
 * Every example is built from this workspace's own fields and its own base URL,
 * so the curl commands are ready to paste rather than a template to adapt.
 * Generic docs get one thing wrong per reader; these get the field names right
 * by construction, and go stale the moment the fields change — which is the
 * point.
 */

function Snippet({ code }: { code: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false);

  return (
    <div className="relative">
      <pre className="lg-scroll overflow-x-auto rounded-lg bg-slate-900 px-4 py-3 pr-12 text-xs leading-relaxed text-slate-100 dark:bg-slate-950">
        <code>{code}</code>
      </pre>

      <button
        type="button"
        className="absolute top-2 right-2 rounded-md p-1.5 text-slate-400 transition hover:bg-slate-800 hover:text-slate-100"
        onClick={() => {
          void navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => {
              setCopied(false);
            }, 1500);
          });
        }}
      >
        {copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardIcon className="h-4 w-4" />}
        <span className="sr-only">Copy</span>
      </button>
    </div>
  );
}

function Endpoint({
  method,
  path,
  children,
}: {
  method: string;
  path: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const tone =
    method === 'GET'
      ? 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300'
      : method === 'DELETE'
        ? 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300'
        : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300';

  return (
    <section className="border-t border-slate-200 pt-5 first:border-0 first:pt-0 dark:border-slate-800">
      <div className="mb-2 flex flex-wrap items-baseline gap-2">
        <span className={`rounded px-1.5 py-0.5 font-mono text-[11px] font-semibold ${tone}`}>
          {method}
        </span>
        <code className="font-mono text-sm text-slate-800 dark:text-slate-100">{path}</code>
      </div>
      {children}
    </section>
  );
}

function Note({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">{children}</p>;
}

interface ApiDocsProps {
  fields: FieldRow[];
  settings?: InventorySettings;
}

export function ApiDocs({ fields, settings }: ApiDocsProps): React.JSX.Element {
  const base = settings?.apiBase ?? window.location.origin;
  const live = fields.filter((field) => !field.archived);
  const label = (settings?.label ?? 'Products').toLowerCase();
  const singular = (settings?.labelSingular ?? 'Product').toLowerCase();

  // A row of real values, so the payloads type-check against the real fields.
  const sample: Record<string, string | number> = {};
  for (const field of live.slice(0, 4)) {
    sample[field.key] =
      field.kind === 'number'
        ? 2024
        : field.kind === 'date'
          ? '2026-03-14'
          : field.kind === 'select'
            ? (field.options[0] ?? 'Example')
            : `Example ${field.label.toLowerCase()}`;
  }

  const body = (extra: Record<string, unknown> = {}): string =>
    JSON.stringify({ externalId: 'SKU-1001', data: sample, ...extra }, null, 2)
      .split('\n')
      .join('\n  ');

  const searchable = live.find((field) => field.searchable)?.key;
  const filterable = live.find((field) => field.filterable);

  return (
    <div className="space-y-6">
      <Endpoint method="—" path="Authentication">
        <Note>
          Every request carries a key in the <code className="lg-code">Authorization</code> header.
          Keys reach the {label} and the fields; they cannot read your conversations, your team or
          your standing requests, and they cannot issue more keys.
        </Note>
        <Snippet
          code={`curl ${base}/api/inventory/items \\
  -H "Authorization: Bearer $LEADGAB_KEY"`}
        />
      </Endpoint>

      <Endpoint method="GET" path="/api/inventory/fields">
        <Note>
          What a {singular} is made of in this workspace. Read this first if you are writing a feed
          — the keys it returns are exactly the keys <code className="lg-code">data</code> accepts.
        </Note>
        <Snippet
          code={`curl ${base}/api/inventory/fields \\
  -H "Authorization: Bearer $LEADGAB_KEY"`}
        />
      </Endpoint>

      <Endpoint method="GET" path="/api/inventory/items">
        <Note>
          Paged, newest first. <code className="lg-code">search</code> matches every searchable
          field at once
          {filterable ? (
            <>
              ; <code className="lg-code">f.{filterable.key}</code> filters on one field exactly.
            </>
          ) : (
            '.'
          )}{' '}
          Follow <code className="lg-code">nextCursor</code> for the next page.
        </Note>
        <Snippet
          code={`# free text across every searchable field
curl -G ${base}/api/inventory/items \\
  -H "Authorization: Bearer $LEADGAB_KEY" \\
  --data-urlencode "search=${searchable ? 'blue' : 'example'}" \\
  --data-urlencode "take=50"
${
  filterable
    ? `
# one field, exactly
curl -G ${base}/api/inventory/items \\
  -H "Authorization: Bearer $LEADGAB_KEY" \\
  --data-urlencode "f.${filterable.key}=${String(sample[filterable.key] ?? 'value')}"
`
    : ''
}
# the next page
curl -G ${base}/api/inventory/items \\
  -H "Authorization: Bearer $LEADGAB_KEY" \\
  --data-urlencode "cursor=1234"`}
        />
      </Endpoint>

      <Endpoint method="POST" path="/api/inventory/items">
        <Note>
          Adds one {singular} and answers with <code className="lg-code">notified</code>: how many
          waiting customers it just told. <code className="lg-code">externalId</code> is your own
          identifier — send it and a later import updates this row instead of adding a second one.
        </Note>
        <Snippet
          code={`curl -X POST ${base}/api/inventory/items \\
  -H "Authorization: Bearer $LEADGAB_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${body()}'`}
        />
      </Endpoint>

      <Endpoint method="PATCH" path="/api/inventory/items/:id">
        <Note>
          Only the keys you send are touched; everything else on the row is left alone. Send{' '}
          <code className="lg-code">null</code> to empty one.
        </Note>
        <Snippet
          code={`curl -X PATCH ${base}/api/inventory/items/1234 \\
  -H "Authorization: Bearer $LEADGAB_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "data": { "${live[0]?.key ?? 'name'}": "New value" }
  }'`}
        />
      </Endpoint>

      <Endpoint method="POST" path="/api/inventory/items/import">
        <Note>
          Up to 500 rows a call. Rows are matched to what is already there by{' '}
          <code className="lg-code">externalId</code> first, then by the identity fields this
          workspace nominated — so re-running a feed updates rather than duplicates. A row that
          fails validation is reported in <code className="lg-code">rejected</code> with its index
          and reason; the rest still land.
        </Note>
        <Snippet
          code={`curl -X POST ${base}/api/inventory/items/import \\
  -H "Authorization: Bearer $LEADGAB_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "items": [
      { "externalId": "SKU-1001", "data": ${JSON.stringify(sample)} },
      { "externalId": "SKU-1002", "data": ${JSON.stringify(sample)} }
    ]
  }'`}
        />
      </Endpoint>

      <Endpoint method="POST" path="/api/inventory/items/import  (replace)">
        <Note>
          How a feed clears and refills. Everything the call writes is stamped with your{' '}
          <code className="lg-code">batchId</code>; anything it did not touch is removed
          afterwards. There is no window where the {label} are empty, and a feed that dies halfway
          leaves the previous stock alone rather than wiping it.
        </Note>
        <Snippet
          code={`curl -X POST ${base}/api/inventory/items/import \\
  -H "Authorization: Bearer $LEADGAB_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "mode": "replace",
    "batchId": "nightly-2026-03-14",
    "items": [
      { "externalId": "SKU-1001", "data": ${JSON.stringify(sample)} }
    ]
  }'`}
        />
      </Endpoint>

      <Endpoint method="DELETE" path="/api/inventory/items/:id">
        <Note>Removes one row outright. To hide it recoverably, use clear or replace instead.</Note>
        <Snippet
          code={`curl -X DELETE ${base}/api/inventory/items/1234 \\
  -H "Authorization: Bearer $LEADGAB_KEY"`}
        />
      </Endpoint>

      <Endpoint method="POST" path="/api/inventory/items/clear">
        <Note>
          Hides every {singular} rather than destroying it, so a mistaken wipe can be undone one row
          at a time with <code className="lg-code">/restore</code>.
        </Note>
        <Snippet
          code={`curl -X POST ${base}/api/inventory/items/clear \\
  -H "Authorization: Bearer $LEADGAB_KEY" \\
  -H "Content-Type: application/json" -d '{}'

# and back again
curl -X POST ${base}/api/inventory/items/1234/restore \\
  -H "Authorization: Bearer $LEADGAB_KEY"`}
        />
      </Endpoint>

      <Endpoint method="—" path="Errors">
        <Note>
          A failure is always JSON in the same shape. <code className="lg-code">fieldErrors</code>{' '}
          names the field that caused it, so a feed can log which column is wrong rather than which
          request.
        </Note>
        <Snippet
          code={`{
  "error": {
    "message": "Check the highlighted fields",
    "code": "bad_request",
    "fieldErrors": { "${live.find((f) => f.kind === 'number')?.key ?? 'quantity'}": "must be a number" }
  }
}`}
        />
      </Endpoint>
    </div>
  );
}
